import type { ColumnDef } from "@tanstack/react-table";
import { useEffect, useState } from "react";
import { Permissions } from "@repo/database";
import { highlightText } from "@repo/utils";
import { createFileRoute } from "@tanstack/react-router";
import { useTranslation } from "react-i18next";

import type { Employee } from "~/providers/employee-provider";
import { SettingsHeader } from "~/components/layout/settings-header";
import { DataTable } from "~/components/miscellaneous/data-table";
import { DataTableColumnHeader } from "~/components/miscellaneous/data-table-column-header";
import { useEmployee } from "~/hooks/use-employee";
import { useEmployeeGroup } from "~/hooks/use-employee-group";
import { useEmployeeManager } from "~/managers/use-employee-manager";
import { usePermissionManager } from "~/managers/use-permission-manager";
import { usePermissionStore } from "~/stores/use-permission-store";

export const Route = createFileRoute("/_main/settings/_layout/employees")({
  component: Employees,
});

function Employees() {
  const { t } = useTranslation("common");

  const { employees } = useEmployee();

  const { employeeGroups } = useEmployeeGroup();

  const { openEmployeeDialog } = useEmployeeManager();

  const columns: ColumnDef<Employee>[] = [
    {
      id: t("name"),
      accessorKey: "fullName",
      filterFn: "includesString",
      size: 200,
      header: ({ column }) => (
        <DataTableColumnHeader column={column} title={t("name")} />
      ),
      cell: ({ row: { original: employee }, table }) => {
        const globalFilter = table.getState().globalFilter as string;

        return (
          <p
            dangerouslySetInnerHTML={{
              __html: highlightText(`${employee.fullName}`, globalFilter) ?? "",
            }}
          ></p>
        );
      },
    },
    {
      id: t("Employee.group"),
      accessorKey: "fullName",
      filterFn: "includesString",
      size: 200,
      header: ({ column }) => (
        <DataTableColumnHeader column={column} title={t("Employee.group")} />
      ),
      cell: ({ row: { original: employee }, table }) => {
        const globalFilter = table.getState().globalFilter as string;

        const employeeGroup = employeeGroups.find(
          (group) => group.id === employee.employeeGroupId,
        );
        return (
          <p
            dangerouslySetInnerHTML={{
              __html:
                highlightText(`${employeeGroup?.name ?? ""}`, globalFilter) ??
                "",
            }}
          ></p>
        );
      },
    },
  ];

  const { hasPermission } = usePermissionManager();
  const { currentEmployee } = usePermissionStore();

  const [permission, setPermission] = useState(false);

  useEffect(() => {
    if (!currentEmployee) {
      setPermission(false);
    }
    hasPermission({
      requiredPermission: Permissions.view_employee,
      label: t("Permissions.view_employee_label"),
    })
      .then((result) => {
        setPermission(result.permission);
      })
      .catch((e) => console.error(e));
  }, [hasPermission, currentEmployee, t]);

  if (!permission) return null;

  return (
    <>
      <SettingsHeader
        buttonAction={() => {
          openEmployeeDialog();
        }}
        buttonLabel={t("new_employee")}
        title={t("employees")}
      />
      <DataTable
        onSelect={openEmployeeDialog}
        data={employees}
        columns={columns}
      />
    </>
  );
}
